import React, { useState, useEffect, useRef } from 'react';
import './chatinterface.css';

// ChatMessage component with typewriter effect (inspired by chatbot.js)
const ChatMessage = ({ message, index }) => {
    const [displayedMessage, setDisplayedMessage] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (message.user === "assistant" && currentIndex < message.message.length) {
            const timeoutId = setTimeout(() => {
                setDisplayedMessage((prevMessage) => prevMessage + message.message.charAt(currentIndex));
                setCurrentIndex(currentIndex + 1);
            }, 20); // Slightly slower for better readability
            
            return () => clearTimeout(timeoutId);
        } else if (message.user === "user") {
            // Show user messages immediately
            setDisplayedMessage(message.message);
        }
    }, [currentIndex, message.message, message.user]);

    // Reset the index when the message changes
    useEffect(() => {
        setCurrentIndex(0);
        setDisplayedMessage("");
    }, [message.message]);

    return (
        <div style={{
            display: 'flex',
            justifyContent: message.user === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: '20px'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                maxWidth: '80%',
                gap: '10px',
                flexDirection: message.user === 'user' ? 'row-reverse' : 'row'
            }}>
                {/* Avatar */}
                <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: message.user === 'user' ? '#0B303B' : '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: message.user === 'user' ? 'white' : '#0B303B',
                    flexShrink: 0,
                    overflow: 'hidden'
                }}>
                    {message.user === 'user' ? (
                        'U'
                    ) : (
                        <img 
                            src="/images/Dave-removebg-preview.png" 
                            alt="Penny" 
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '50%'
                            }}
                        />
                    )}
                </div>
                
                {/* Message bubble */}
                <div style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    backgroundColor: message.user === 'user' ? '#0B303B' : 'white',
                    color: message.user === 'user' ? 'white' : '#333',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.5
                }}>
                    {/* Name header */}
                    <div style={{
                        fontWeight: '600',
                        marginBottom: '8px',
                        fontSize: '14px',
                        color: message.user === 'user' ? 'rgba(255,255,255,0.8)' : '#0B303B'
                    }}>
                        {message.user === 'user' ? 'You' : 'Penny'}
                    </div>
                    
                    {/* Message content with typewriter effect for assistant */}
                    <div>
                        {message.user === "user" ? message.message : displayedMessage}
                    </div>
                </div>
            </div>
        </div>
    );
};

function ChatInterface({ analysisResults, isRecipeCreation = false, style = {}, onRecipesConfirmed }) {
    const [message, setMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([
        {
            user: 'assistant',
            message: "Hi! I'm Penny 🍳\n\nI'll help you create recipes based on what you want to eat and what you have at home.\n\nWhat type of cuisine or dish are you in the mood for today?"
        }
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const chatMessagesRef = useRef(null);

    // Get API base URL from environment variable
    const apiBaseUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim() || isLoading) return;

        // Add user message to chat (using chatbot.js structure)
        const userMessage = { user: 'user', message: message };
        setChatHistory(prev => [...prev, userMessage]);
        setMessage('');
        setIsLoading(true);

        try {
            // Convert chatHistory to API format
            const apiHistory = chatHistory.map(msg => ({
                role: msg.user === 'user' ? 'user' : 'assistant',
                content: msg.message
            }));

            const response = await fetch(`${apiBaseUrl}/api/claude/chat/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message,
                    history: apiHistory,
                    context: analysisResults, // Include the recipe analysis
                    isRecipeCreation: isRecipeCreation // Flag for recipe creation mode
                })
            });

            if (!response.ok) {
                throw new Error('Failed to get response');
            }

            const data = await response.json();
            setChatHistory(prev => [...prev, { user: 'assistant', message: data.response }]);
            
            // Check if recipes were confirmed and trigger transition to recipe analysis
            if (data.recipes_confirmed && data.recipes_data && onRecipesConfirmed) {
                // Add a delay to let the user read the confirmation message
                setTimeout(() => {
                    onRecipesConfirmed(data.recipes_data);
                }, 3000); // 3 seconds to read the confirmation message
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setChatHistory(prev => [...prev, { 
                user: 'assistant', 
                message: 'Sorry, I encountered an error processing your message. Please try again.' 
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [chatHistory, isLoading]);

    // Clear chat function (inspired by chatbot.js)
    const clearChat = () => {
        setChatHistory([
            {
                user: 'assistant',
                message: "Hi! I'm Penny 🍳\n\nI'll help you create recipes based on what you want to eat and what you have at home.\n\nWhat type of cuisine or dish are you in the mood for today?"
            }
        ]);
    };

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e0e0e0',
            padding: '30px',
            minHeight: '500px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
            ...style
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                <h2 style={{
                    color: '#0B303B',
                    fontSize: '1.8rem',
                    fontWeight: '500',
                    margin: 0,
                    flex: 1,
                    textAlign: 'center'
                }}>
                    Create Your Recipe with Penny
                </h2>
                {chatHistory.length > 1 && (
                    <button
                        onClick={clearChat}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: 'transparent',
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px',
                            color: '#666',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f5f5f5';
                            e.target.style.borderColor = '#0B303B';
                            e.target.style.color = '#0B303B';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.color = '#666';
                        }}
                    >
                        <span>+</span>
                        New Chat
                    </button>
                )}
            </div>
            
            {/* Chat Messages Area */}
            <div 
                ref={chatMessagesRef}
                className="chat-messages-container"
                style={{
                    height: '400px',
                    overflowY: 'auto',
                    marginBottom: '20px',
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                }}>
                {chatHistory.map((msg, index) => (
                    <ChatMessage key={index} message={msg} index={index} />
                ))}
                {isLoading && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            maxWidth: '80%',
                            gap: '10px'
                        }}>
                            {/* Penny Avatar */}
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: '#f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                color: '#0B303B',
                                flexShrink: 0,
                                overflow: 'hidden'
                            }}>
                                <img 
                                    src="/images/Dave-removebg-preview.png" 
                                    alt="Penny" 
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '50%'
                                    }}
                                />
                            </div>
                            
                            {/* Loading message */}
                            <div style={{
                                padding: '12px 16px',
                                borderRadius: '12px',
                                backgroundColor: 'white',
                                color: '#333',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                                <div style={{
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    color: '#0B303B'
                                }}>
                                    Penny
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                }}>
                                    <span>Cooking up a response</span>
                                    <div style={{
                                        display: 'flex',
                                        gap: '2px'
                                    }}>
                                        <div style={{
                                            width: '4px',
                                            height: '4px',
                                            backgroundColor: '#0B303B',
                                            borderRadius: '50%',
                                            animation: 'bounce 1.4s ease-in-out infinite both'
                                        }}></div>
                                        <div style={{
                                            width: '4px',
                                            height: '4px',
                                            backgroundColor: '#0B303B',
                                            borderRadius: '50%',
                                            animation: 'bounce 1.4s ease-in-out 0.16s infinite both'
                                        }}></div>
                                        <div style={{
                                            width: '4px',
                                            height: '4px',
                                            backgroundColor: '#0B303B',
                                            borderRadius: '50%',
                                            animation: 'bounce 1.4s ease-in-out 0.32s infinite both'
                                        }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Chat Input */}
            <form onSubmit={handleSubmit} style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center'
            }}>
                <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe your recipe idea or ask for cooking help..."
                    disabled={isLoading}
                    style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        outline: 'none',
                        transition: 'border-color 0.3s',
                        backgroundColor: isLoading ? '#f5f5f5' : 'white'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#0B303B';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e0e0e0';
                    }}
                />
                <button 
                    type="submit" 
                    disabled={!message.trim() || isLoading}
                    style={{
                        padding: '12px 20px',
                        backgroundColor: (!message.trim() || isLoading) ? '#ccc' : '#0B303B',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '500',
                        cursor: (!message.trim() || isLoading) ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => {
                        if (!(!message.trim() || isLoading)) {
                            e.target.style.backgroundColor = '#0a2832';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!(!message.trim() || isLoading)) {
                            e.target.style.backgroundColor = '#0B303B';
                        }
                    }}
                >
                    {isLoading ? 'Sending...' : 'Send'}
                </button>
            </form>
        </div>
    );
}

export default ChatInterface; 